/**
 * Status effect state (TRD-05 §7).
 *
 * TRD-01 owns the shape and lifecycle; TRD-05 owns the rules (stacking, magnitudes,
 * damage). Defined here because `Enemy` embeds it and entity storage has to know how big
 * an enemy is and how to reset one on pool reuse.
 *
 * Durations are ABSOLUTE tick numbers, never countdowns (TRD-05 R-05-27). A countdown
 * has to be decremented on every enemy every tick, and it drifts when a status is applied
 * partway through a tick. An expiry tick is compared, not mutated.
 */

import { NO_TARGET, type EntityId } from './types.js';

/** Sentinel for "not active". Tick 0 is a real tick, so 0 cannot mean absent. */
export const INACTIVE = -1;

/** No tower has applied poison to this enemy — the same sentinel entity ids use. */
export const NO_SOURCE: EntityId = NO_TARGET;

export interface StatusState {
  /**
   * `source` is the tower whose hit applied the most recent stack, for damage
   * attribution only — poison ticks are sourceless in the damage pipeline and so were
   * credited to NOBODY, which hid roughly 9% of a run's damage and made a Venom board
   * read as dead weight next to whatever was landing direct hits.
   */
  poison: { stacks: number; expiresAtTick: number; nextTickAt: number; source: EntityId };
  chill: { magnitude: number; expiresAtTick: number };
  shock: { stacks: number; expiresAtTick: number };
  wither: { magnitude: number; expiresAtTick: number };
  mark: { expiresAtTick: number };
  stun: { expiresAtTick: number };
  freeze: { expiresAtTick: number };
}

export function createStatusState(): StatusState {
  return {
    poison: { stacks: 0, expiresAtTick: INACTIVE, nextTickAt: INACTIVE, source: NO_SOURCE },
    chill: { magnitude: 0, expiresAtTick: INACTIVE },
    shock: { stacks: 0, expiresAtTick: INACTIVE },
    wither: { magnitude: 0, expiresAtTick: INACTIVE },
    mark: { expiresAtTick: INACTIVE },
    stun: { expiresAtTick: INACTIVE },
    freeze: { expiresAtTick: INACTIVE },
  };
}

/**
 * Clear in place, for pool reuse.
 *
 * Reassigning `enemy.statuses = createStatusState()` would allocate eight objects per
 * spawn — with 400 enemies per wave across 25 waves that is real garbage-collector
 * pressure inside the tick loop (TRD-01 R-01-12).
 */
export function resetStatusState(s: StatusState): void {
  s.poison.stacks = 0;
  s.poison.expiresAtTick = INACTIVE;
  s.poison.nextTickAt = INACTIVE;
  s.chill.magnitude = 0;
  s.chill.expiresAtTick = INACTIVE;
  s.shock.stacks = 0;
  s.shock.expiresAtTick = INACTIVE;
  s.wither.magnitude = 0;
  s.wither.expiresAtTick = INACTIVE;
  s.mark.expiresAtTick = INACTIVE;
  s.stun.expiresAtTick = INACTIVE;
  s.freeze.expiresAtTick = INACTIVE;
}

export function isStatusActive(expiresAtTick: number, tick: number): boolean {
  return expiresAtTick !== INACTIVE && expiresAtTick > tick;
}
